<template>
  <b-modal
    id="ftp-credentials-modal"
    centered
    ok-only
    ok-title="Chiudi"
    title="Dati FTP"
    @show="load"
  >
    <div
      v-if="loading"
      class="d-flex align-items-center justify-content-center py-2"
    >
      <b-spinner
        small
        class="mr-1"
        label="Caricamento"
      />
      <span class="text-muted">Caricamento dei dati FTP...</span>
    </div>

    <div v-else-if="error">
      <b-alert
        variant="danger"
        show
      >
        <div class="alert-body">
          Impossibile caricare i dati FTP.
        </div>
      </b-alert>
      <b-button
        variant="outline-primary"
        @click="load"
      >
        Riprova
      </b-button>
    </div>

    <div v-else-if="account && !account.has_account">
      <p class="mb-50">
        Nessun accesso FTP configurato per il tuo utente.
      </p>
      <p class="text-muted small mb-0">
        Se ti serve caricare file via FTP, chiedi a un amministratore di abilitarlo.
      </p>
    </div>

    <div v-else-if="account">
      <div
        v-if="!account.active"
        class="mb-1"
      >
        <b-badge variant="warning">
          Disattivato
        </b-badge>
        <p class="text-muted small mt-50 mb-0">
          L'accesso FTP del tuo utente è disattivato: contatta un amministratore.
        </p>
      </div>

      <b-form-group
        v-for="field in fields"
        :id="'ftp-credentials-' + field.key + '-group'"
        :key="field.key"
        :label="field.label"
      >
        <b-input-group>
          <!-- testo in sola lettura e non input: lo username contiene l'id del tenant e su telefono deve andare a capo -->
          <div
            :id="'ftp-credentials-' + field.key"
            class="form-control h-auto text-monospace text-break ftp-credentials-value"
            role="textbox"
            aria-readonly="true"
            :aria-labelledby="'ftp-credentials-' + field.key + '-group__BV_label_'"
            tabindex="0"
            @focus="selectAll"
            v-text="field.value"
          />
          <b-input-group-append v-if="canCopy">
            <b-button
              variant="outline-secondary"
              :aria-label="field.copyLabel"
              title="Copia"
              @click="copy(field.value)"
            >
              <feather-icon
                icon="CopyIcon"
                size="16"
              />
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-form-group>

      <p class="text-muted small mb-50">
        La password è la stessa con cui accedi ad Archistar.
      </p>
      <p class="text-muted small mb-0">
        Dopo il caricamento, apri Staging per importare i file in Archistar.
      </p>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BAlert, BButton, BBadge, BFormGroup, BInputGroup, BInputGroupAppend,
} from 'bootstrap-vue'
import { RepositoryFactory } from '@/api/RepositoryFactory'
import apiErrors from '@/helpers/api-errors'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ftpRepository = RepositoryFactory.get('ftp')

export default {
  name: 'FtpCredentialsModal',
  components: {
    BModal,
    BSpinner,
    BAlert,
    BButton,
    BBadge,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
  },
  data() {
    return {
      loading: false,
      error: false,
      account: null,
    }
  },
  computed: {
    canCopy() {
      return typeof navigator !== 'undefined'
        && !!navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
    },
    fields() {
      if (!this.account || !this.account.has_account) {
        return []
      }
      return [
        {
          key: 'host', label: 'Host', value: String(this.account.host), copyLabel: 'Copia host',
        },
        {
          key: 'port', label: 'Porta', value: String(this.account.port), copyLabel: 'Copia porta',
        },
        {
          key: 'username', label: 'Nome utente', value: String(this.account.username), copyLabel: 'Copia nome utente',
        },
      ]
    },
  },
  methods: {
    load() {
      this.loading = true
      this.error = false
      this.account = null
      return ftpRepository.credentials()
        .then(response => {
          if (!response.data || !response.data.code || !response.data.data) {
            this.error = true
            return
          }
          this.account = response.data.data
        })
        .catch(error => {
          apiErrors.logApiError(error)
          this.error = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    selectAll(event) {
      if (event && event.target && typeof window.getSelection === 'function') {
        window.getSelection().selectAllChildren(event.target)
      }
    },
    copy(value) {
      return navigator.clipboard.writeText(value)
        .then(() => {
          this.toast('Copiato negli appunti', 'CheckIcon', 'success')
        })
        .catch(() => {
          this.toast('Copia non riuscita: seleziona il testo e copialo a mano', 'AlertTriangleIcon', 'danger')
        })
    },
    toast(title, icon, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, icon, variant,
        },
      },
      {
        position: 'top-center',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import 'bootstrap/scss/functions';
@import '~@core/scss/base/bootstrap-extended/variables';
@import 'bootstrap/scss/variables';
@import '~@core/scss/base/components/variables-dark';

// il tema scuro colora solo input e textarea: qui il campo e' un div
.dark-layout .ftp-credentials-value {
  background-color: $theme-dark-input-bg;
  color: $theme-dark-body-color;
  border-color: $theme-dark-input-border-color;
}
</style>
